import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import theme, { themes, Theme } from '../themes';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { track } from '@vercel/analytics';
import {
  Container,
  Title,
  ButtonContainer,
  Button,
  ModeContainer,
  ModeButton,
  TimerWrapper,
  TimerText,
  Card,
  SectionTitle,
  StatItem,
  StatLabel,
  StatValue,
  AppRow,
  MainContent,
  CompletionStats,
  RightRail,
  CalendarTitle,
  CalendarContainer,
  CountsRow,
  CountDot,
  SprinkleBackground,
  Awning
} from './timer/styles';
import { formatTime, getTodayLocalString, isSameLocalDay, getLocalDateString } from './timer/utils';
import { TimerProps, CompletionCount } from './timer/types';
import { DonutProgress } from './timer/DonutProgress';

const modeColors = {
  study: '#FF6B6B',
  break: '#95e1d3',
  longBreak: '#FFD93D',
};

export const Timer: React.FC<TimerProps> = ({ initialMode = theme.defaultMode }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(themes.donut);
  const [timeLeft, setTimeLeft] = useState(currentTheme.modes[initialMode as keyof typeof currentTheme.modes].duration * 60);
  const [originalTime, setOriginalTime] = useState(currentTheme.modes[initialMode as keyof typeof currentTheme.modes].duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [currentMode, setCurrentMode] = useState<keyof typeof currentTheme.modes>(initialMode as keyof typeof currentTheme.modes);
  
  const completionRef = useRef<{[key: string]: boolean}>({});
  
  const [completionCounts, setCompletionCounts] = useState<CompletionCount>(() => {
    const saved = localStorage.getItem('completionCounts');
    const today = getTodayLocalString();
    if (saved) {
      const parsed = JSON.parse(saved) as CompletionCount;
      if (parsed.date !== today) {
        return { study: 0, break: 0, longBreak: 0, date: today };
      }
      return parsed;
    }
    return { study: 0, break: 0, longBreak: 0, date: today };
  });

  const [history, setHistory] = useState<{[date: string]: CompletionCount}>({});

  useEffect(() => {
    localStorage.setItem('completionCounts', JSON.stringify(completionCounts));
  }, [completionCounts]);

  useEffect(() => {
    const saved = localStorage.getItem('completionHistory');
    if (saved) {
      setHistory(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    const today = getTodayLocalString();
    setHistory(prev => {
      const updated = { ...prev, [today]: completionCounts };
      localStorage.setItem('completionHistory', JSON.stringify(updated));
      return updated;
    });
  }, [completionCounts]);

  useEffect(() => {
    setCurrentTheme(themes.donut);
  }, []);

  useEffect(() => {
    setTimeLeft(currentTheme.modes[currentMode].duration * 60);
    setOriginalTime(currentTheme.modes[currentMode].duration * 60);
  }, [currentTheme, currentMode]);

  const playSound = useCallback(() => {
    const audio = new Audio('https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg');
    audio.play();
  }, []);

  const showNotification = useCallback(() => {
    if (Notification.permission === 'granted') {
      new Notification('Time\'s Up! 🎉', {
        body: 'Great job! Take a break!',
      });
    }
  }, []);

  const startTimer = useCallback(() => {
    if (timeLeft === 0) {
      setTimeLeft(originalTime);
      completionRef.current[currentMode] = false;
    }
    track('timer_started', { mode: currentMode, timeLeft });
    setIsRunning(true);
  }, [timeLeft, originalTime, currentMode]);

  const pauseTimer = useCallback(() => {
    track('timer_paused', { mode: currentMode, timeLeft });
    setIsRunning(false);
  }, [currentMode, timeLeft]);

  const resetTimer = useCallback(() => {
    track('timer_reset', { mode: currentMode });
    setIsRunning(false);
    setTimeLeft(originalTime);
    completionRef.current[currentMode] = false;
  }, [originalTime, currentMode]);

  const setTime = useCallback((mode: keyof typeof currentTheme.modes) => {
    const seconds = currentTheme.modes[mode].duration * 60;
    track('mode_changed', { 
      from: currentMode, 
      to: mode,
      fromModeName: currentTheme.modes[currentMode].name,
      toModeName: currentTheme.modes[mode].name,
      previousTimeLeft: timeLeft,
      wasRunning: isRunning 
    });
    setTimeLeft(seconds);
    setOriginalTime(seconds);
    setIsRunning(false);
    setCurrentMode(mode);
    completionRef.current[mode] = false;
  }, [currentTheme, currentMode, timeLeft, isRunning]);

  useEffect(() => {
    let intervalId: number;

    if (isRunning && timeLeft > 0) {
      intervalId = window.setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId);
            playSound();
            showNotification();
            setIsRunning(false);
            
            const modeKey = currentMode;
            if (!completionRef.current[modeKey]) {
              track('timer_completed', { 
                mode: modeKey, 
                duration: currentTheme.modes[modeKey].duration 
              });
              setCompletionCounts(prev => ({
                ...prev,
                [modeKey]: prev[modeKey] + 1
              }));
              completionRef.current[modeKey] = true;
            }
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRunning, timeLeft, playSound, showNotification, currentMode, currentTheme.modes]);

  useEffect(() => {
    if (isRunning && timeLeft === 0) {
      setTimeLeft(originalTime);
      completionRef.current[currentMode] = false;
    }
  }, [isRunning, timeLeft, originalTime, currentMode]);

  const progress = 100 - (timeLeft / originalTime) * 100;

  return (
    <>
      <SprinkleBackground />
      <AppRow>
        <Card as={CompletionStats} style={{minWidth: 220, maxWidth: 260}}>
          <SectionTitle>Today's Counts</SectionTitle>
          <StatItem>
            <StatLabel>Study Sessions:</StatLabel>
            <StatValue mode="study">{completionCounts.study}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Break Sessions:</StatLabel>
            <StatValue mode="break">{completionCounts.break}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Long Breaks:</StatLabel>
            <StatValue mode="longBreak">{completionCounts.longBreak}</StatValue>
          </StatItem>
        </Card>
        <Card as={Container} style={{
          maxWidth: 700,
          minWidth: 700,
          flex: 2,
          marginLeft: 30,
          marginRight: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '30px 20px',
          gap: '20px',
          position: 'relative'
        }}>
          <Awning />
          <Title>{currentTheme.name} Timer!</Title>
          <MainContent>
            <TimerWrapper>
              <DonutProgress 
                progress={progress}
                size={440}
                strokeWidth={currentTheme.donut.strokeWidth}
                color={currentTheme.donut.progressColor}
                bgColor={currentTheme.donut.backgroundColor}
              />
              <TimerText
                animate={{ 
                  scale: isRunning ? [1, 1.05, 1] : 1 
                }}
                transition={{ 
                  duration: 2, 
                  repeat: isRunning ? Infinity : 0,
                  ease: "easeInOut"
                }}
              >
                {formatTime(timeLeft)}
              </TimerText>
            </TimerWrapper>
            <ButtonContainer>
              <Button
                onClick={startTimer}
                disabled={isRunning}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                Start! 
              </Button>
              <Button
                onClick={pauseTimer}
                disabled={!isRunning}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                Pause
              </Button>
              <Button
                onClick={resetTimer}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                Reset
              </Button>
            </ButtonContainer>
            <ModeContainer>
              {Object.entries(currentTheme.modes).map(([mode, config]) => (
                <ModeButton
                  key={mode}
                  onClick={() => setTime(mode as keyof typeof currentTheme.modes)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  isActive={currentMode === mode}
                >
                  {config.name} {config.emoji}
                </ModeButton>
              ))}
            </ModeContainer>
          </MainContent>
        </Card>
        <Card as={RightRail} style={{minWidth: 400, maxWidth: 540}}>
          <SectionTitle>Monthly Progress</SectionTitle>
          <CalendarTitle style={{display: 'none'}} />
          <CalendarContainer>
            <Calendar
              tileContent={({ date, view }) => {
                if (view !== 'month') return null;
                const dateStr = getLocalDateString(date);
                const counts = history[dateStr] || { study: 0, break: 0, longBreak: 0, date: dateStr };
                if (counts.study + counts.break + counts.longBreak === 0) return null;
                return (
                  <CountsRow>
                    <CountDot color={modeColors.study}>{counts.study > 0 ? counts.study : ''}</CountDot>
                    <CountDot color={modeColors.break}>{counts.break > 0 ? counts.break : ''}</CountDot>
                    <CountDot color={modeColors.longBreak}>{counts.longBreak > 0 ? counts.longBreak : ''}</CountDot>
                  </CountsRow>
                );
              }}
              tileClassName={({ date }) => isSameLocalDay(date, new Date()) ? 'react-calendar__tile--now' : undefined}
              calendarType="gregory"
              prev2Label={null}
              next2Label={null}
            />
          </CalendarContainer>
        </Card>
      </AppRow>
    </>
  );
};

export default Timer;